import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CustomValue } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.styled';
import { TJobRenderComponentProps } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.types';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import { useReadProjectsByIdsQuery } from '@/shared/redux/api/assets/projects/projects-api';

export const JobProject: React.FC<TJobRenderComponentProps> = ({
  job,
  truncate,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canReadProject = usePermissions({
    entity: 'Project',
    action: 'read',
    attribute: { id: job?.projectIds?.[0] },
  });
  const { data: projectsData, isLoading: projectsLoading } =
    useReadProjectsByIdsQuery(
      { projectIds: job?.projectIds! },
      { skip: !job?.projectIds?.length },
    );

  const project = projectsData?.readProjectsByIds?.items?.[0];

  const navigateToProject = () => {
    if (!project) {
      return;
    }
    navigate(`/projects/${project.projectId}`);
  };

  const renderLabel = () => {
    const label =
      project?.name ??
      (canReadProject
        ? t('projects:deleted_project')
        : t('projects:restricted_project'));

    if (truncate) {
      return <TruncatedText variant="p7" maxLength={32} text={label} />;
    }

    return <Typography variant="p7">{label}</Typography>;
  };

  if (!job?.projectIds?.[0]) {
    return null;
  }

  if (projectsLoading) {
    return <Skeleton />;
  }

  return (
    <CustomValue onClick={navigateToProject} $clickable={!!project}>
      <ProjectsIcon
        size="1rem"
        variant={canReadProject ? 'primary' : 'light'}
      />
      {renderLabel()}
    </CustomValue>
  );
};
